<template>
  <vs-popup
    class="report-download-dialog-wrapper"
    :active.sync="isActive"
    :button-close-hidden="true"
  >
    <div class="dialog-header">
      <h3 class="dialog-title">{{ title }}</h3>
      <feather-icon icon="XIcon" class="close-icon" @click="onClose" />
    </div>
    <div class="dialog-content">
      <div class="dialog-subtitle">
        ダウンロードするファイルを選択してください。
      </div>

      <div class="list-container">
        <div
          v-for="csv in sortedCsvList"
          :key="csv.id"
          :class="['item', getItemClass(csv.id)]"
          @click="onSelectCsv(csv)"
        >
          <div class="item-left">
            <span class="item-name">
              {{ csv.label }}
            </span>
          </div>
          <feather-icon icon="DownloadIcon" class="download-icon" />
        </div>
      </div>
    </div>
    <div class="dialog-footer">
      <vs-button class="close-button" :color="color" @click="onClose">
        {{ closeText }}
      </vs-button>
    </div>
  </vs-popup>
</template>

<script>
import { ColorMixin } from "@/mixins";
import operationReportService from "@/services/operations/operationReportService";
const { AISEA_COLOR } = ColorMixin.data();

const EVENTS = {
  SELECT: "select",
  CLOSE: "close",
};

export default {
  name: "ReportDownloadDialog",
  props: {
    title: {
      type: String,
      default() {
        return "ダウンロード";
      },
    },
    color: {
      type: String,
      default: AISEA_COLOR.BLUE,
    },
    closeText: {
      type: String,
      default() {
        return "閉じる";
      },
    },
    active: {
      type: Boolean,
      default: false,
    },
    csvList: {
      type: Array,
      default: () => [],
    },
    vesselName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      downloadingIds: {}, // ダウンロード中のCSV IDを管理
      completedIds: {}, // ダウンロード完了したCSV IDを管理
    };
  },
  computed: {
    isActive: {
      get() {
        return this.active;
      },
      set(value) {
        if (!value) {
          this.onClose();
        }
      },
    },
    sortedCsvList() {
      return [...this.csvList].sort((a, b) => {
        const orderA = a.sort_order;
        const orderB = b.sort_order;
        return orderA - orderB;
      });
    },
  },
  watch: {
    active(newVal) {
      if (!newVal) {
        this.downloadingIds = {};
        this.completedIds = {};
      }
    },
  },
  methods: {
    async onSelectCsv(csv) {
      // 既にダウンロード中の場合は何もしない
      if (this.downloadingIds[csv.id]) return;

      const resolvedFileName = csv.file_name.replace(
        /{vessel_name}/g,
        this.vesselName || ""
      );

      try {
        // ダウンロード中状態に設定
        this.$set(this.downloadingIds, csv.id, true);

        // APIからファイルをダウンロード
        const response = await operationReportService.downloadReportFile({
          aoEnterpriseId: this.$route.params.aoEnterpriseId,
          reportId: this.$route.params.reportId,
          aoEnterpriseCsvId: csv.id,
        });

        // Blobからファイルをダウンロード
        const blob = new Blob([response.data], { type: csv.content_type });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = resolvedFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // ダウンロード完了状態に設定
        this.$delete(this.downloadingIds, csv.id);
        this.$set(this.completedIds, csv.id, true);

        // 成功通知
        this.$vs.notify({
          title: "ダウンロードが成功しました",
          text: `「${resolvedFileName}」をダウンロードしました。`,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      } catch (error) {
        console.error("Download failed:", error);
        // ダウンロード中状態を解除
        if (this.downloadingIds[csv.id]) {
          this.$delete(this.downloadingIds, csv.id);
        }

        // 失敗通知
        this.$vs.notify({
          title: "ダウンロードに失敗しました",
          text: `エラー発生しました。「${resolvedFileName}」のダウンロードに失敗しました。`,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      }
    },
    onClose() {
      this.$emit(EVENTS.CLOSE);
    },
    getItemClass(csvId) {
      if (this.downloadingIds[csvId]) return "item-downloading";
      if (this.completedIds[csvId]) return "item-completed";
      return "";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.report-download-dialog-wrapper {
  display: flex;
  flex-direction: column;

  // カスタムヘッダー
  .dialog-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-top: 6px;
    background-color: #f8f8f8;
    border-radius: 8px 8px 0 0;

    .dialog-title {
      color: $aisea-dark-gray;
      font-family: $aisea-font-family;
      font-weight: 600;
      font-size: 18px;
      line-height: 20px;
      letter-spacing: 0%;
      text-align: right;
      vertical-align: middle;
    }

    .close-icon {
      width: 24px;
      height: 24px;
      color: $aisea-gray-black;
      cursor: pointer;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .dialog-content {
    margin: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow: auto;

    // サブタイトル
    .dialog-subtitle {
      font-family: $aisea-font-family;
      color: $aisea-black;
      font-weight: 400;
      font-size: 16px;
      line-height: 28.8px;
      letter-spacing: 0%;
    }

    // リストコンテナ
    .list-container {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px;
        background-color: #f8f8f8;
        cursor: pointer;
        transition: background-color 0.2s;
        border-radius: 6px;

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background-color: rgba($aisea-light-gray, 0.5);
        }

        // ダウンロード中の背景色
        &.item-downloading {
          background-color: $aisea-light-white;
          cursor: not-allowed;
        }

        // ダウンロード完了時の背景色
        &.item-completed {
          background-color: #eaf8fe;
        }

        .item-left {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;

          .check-icon {
            width: 24px;
            height: 24px;
            font-weight: 600;
            color: $aisea-blue;
          }

          .item-name {
            font-family: $aisea-font-family;
            color: $aisea-blue;
            font-weight: 400;
            font-size: 14px;
            line-height: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .download-icon {
          width: 24px;
          height: 24px;
          color: $aisea-blue;
        }
      }
    }
  }

  // カスタムフッター
  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    padding: 0px 24px 20px 24px;

    .close-button {
      padding: 8px 24px;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
    }
  }

  ::v-deep {
    .vs-popup {
      width: 500px;
      max-height: 60vh;
      display: flex;
      flex-direction: column;

      // デフォルトのタイトルを非表示
      .vs-popup--title {
        display: none;
      }

      .vs-popup--content {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        flex: 1; // ★追加: 残りの高さをすべて占有させる
        min-height: 0; // ★重要: Flex子要素の高さ縮小を許可する
        display: flex; // ★追加
        flex-direction: column; // ★追加
      }
    }
  }
}
</style>
